import React, {
  MouseEventHandler,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import {
  differenceBy,
  find,
  first,
  isEmpty,
  reject,
  sortBy,
  uniqBy,
} from "lodash/fp";

import { Divider, Typography } from "@mui/material";

import ApplyToSeries from "@app/analytics/components/ApplyToSeries";
import Tag from "@app/analytics/components/Tag";
import type { TagColor } from "@app/analytics/components/Tag/utils";
import type { Tag as ITag } from "@app/common/types";
import { replaceWhere } from "@app/utils/arrays";
import useAnchorElement, {
  UseAnchorElement,
} from "@app/utils/hooks/useAnchorElement";

import Stack, { Row } from "@rui/Stack";
import TooltipIcon from "@rui/TooltipIcon";

import SelectableTag from "./SelectableTag";
import {
  ActionButton,
  AdornedTextField,
  HeaderButton,
  Popover,
  PopoverContent,
  PopoverHeader,
  TagList,
  TagListContainer,
} from "./styles";
import TagInput from "./TagInput";
import { getSuggestedTags, isNewTag, isNewSuggestedTag } from "./utils";

interface ControlProps {
  onClick: MouseEventHandler<HTMLElement>;
}

export interface ManageTagsPopoverProps {
  /** All tags currently available for the user */
  allTags: ITag[];
  /** All tags currently applied to this entity */
  appliedTags: ITag[];
  canApplyToSeries?: boolean;
  onSave: (tags: ITag[], applyToSeries?: boolean) => void;
  popoverProps?: Pick<UseAnchorElement, "onClose" | "open"> & {
    anchorEl: UseAnchorElement["anchorElement"];
  };
  renderControl?: (props: ControlProps) => React.ReactNode;
}

const ManageTagsPopover = ({
  allTags,
  appliedTags,
  canApplyToSeries = false,
  onSave,
  renderControl,
  popoverProps,
}: ManageTagsPopoverProps) => {
  const [inputValue, setInputValue] = useState("");
  const [suggestedTags, setSuggestedTags] = useState<ITag[]>([]);

  // intermediate "allTags" state so that changes only persist on save
  const [unsavedAllTags, setUnsavedAllTags] = useState<ITag[]>(allTags);
  // intermediate "appliedTags" state so that changes only persist on save
  const [unsavedAppliedTags, setUnsavedAppliedTags] =
    useState<ITag[]>(appliedTags);
  const [applyToSeries, setApplyToSeries] = useState(true);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const { open, onClose, onClick, anchorElement } = useAnchorElement();

  const unappliedTags = sortBy(
    "name",
    differenceBy("id", unsavedAllTags, unsavedAppliedTags)
  );

  useEffect(() => {
    resetInitialState();
  }, [allTags.length, appliedTags.length, open]);

  const focusInputElement = () => inputRef.current?.click();

  const handleInputChange: React.ChangeEventHandler<HTMLInputElement> = (
    event
  ) => {
    const inputValue = event.target.value;
    const suggestedTag = getSuggestedTags(
      inputValue,
      unsavedAllTags,
      suggestedTags,
      unsavedAppliedTags
    );
    setInputValue(inputValue);
    setSuggestedTags(suggestedTag);
  };

  const handleTagColorChange = (tagId: ITag["id"]) => (color: TagColor) => {
    setSuggestedTags((tags) => {
      const tagToUpdate = find({ id: tagId }, tags);
      if (!tagToUpdate) return tags;

      const updatedTag: ITag = { ...tagToUpdate, color: color.backgroundColor };
      return replaceWhere<ITag>(tags, updatedTag, ({ id }) => id === tagId);
    });
  };

  const clearInputValue = useCallback(() => {
    setInputValue("");
    setSuggestedTags([]);
  }, []);

  const handleSelectSuggestedTag = (tag: ITag) => {
    if (isNewTag(tag)) {
      setUnsavedAllTags((tags) => uniqBy("id", [...tags, tag]));
    }
    setUnsavedAppliedTags((tags) => uniqBy("id", [...tags, tag]));
    clearInputValue();
    focusInputElement();
  };

  const handleSelectExistingTag = (tag: ITag) => {
    setUnsavedAppliedTags((tags) => uniqBy("id", [...tags, tag]));
    focusInputElement();
  };

  const handleRemoveTag = (tag: ITag) => () => {
    setUnsavedAppliedTags((tags) => reject({ id: tag.id }, tags));
  };

  const handleRemoveAll = useCallback(() => {
    setUnsavedAppliedTags([]);
  }, []);

  const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (
    event
  ) => {
    switch (event.key) {
      case "Escape":
        event.stopPropagation();
        event.preventDefault();
        clearInputValue();
        break;
      case "Enter": {
        event.stopPropagation();
        event.preventDefault();
        const suggestedTag = first(suggestedTags);
        if (suggestedTag) {
          handleSelectSuggestedTag(suggestedTag);
        }
        break;
      }
      default:
        break;
    }
  };

  const resetInitialState = () => {
    setUnsavedAppliedTags(appliedTags);
    setUnsavedAllTags(allTags);
    setApplyToSeries(true);
  };

  const closePopover = useCallback(() => {
    popoverProps ? popoverProps.onClose() : onClose();
    clearInputValue();
  }, []);

  const handleClose = () => {
    closePopover();
  };

  const handleSave = () => {
    onSave(unsavedAppliedTags, canApplyToSeries ? applyToSeries : undefined);
    closePopover();
  };

  const openPopover: typeof onClick = (event) => {
    onClick(event);
    // short timeout to give time for the element to appear before trying to focus it
    setTimeout(focusInputElement, 100);
  };

  const handleToggleApplyToSeries = useCallback(
    (event: React.SyntheticEvent, checked: boolean) => {
      setApplyToSeries(checked);
    },
    []
  );

  return (
    <>
      {renderControl?.({ onClick: openPopover })}
      <Popover
        open={open}
        anchorEl={anchorElement}
        {...popoverProps}
        onClose={handleClose}
      >
        <PopoverContent spacing={1}>
          <PopoverHeader variant="apart">
            <Typography variant="bodySecondaryMed">
              Tags{appliedTags.length ? ` (${appliedTags.length})` : ""}
            </Typography>
            <HeaderButton
              color="error"
              onClick={handleRemoveAll}
              disabled={unsavedAppliedTags.length < 1}
            >
              Remove all
            </HeaderButton>
          </PopoverHeader>
          <AdornedTextField onClick={focusInputElement} autoFocus>
            {unsavedAppliedTags.map((tag) => (
              <Tag
                sizing="padded"
                key={tag.id}
                tag={tag}
                onAction={handleRemoveTag(tag)}
              />
            ))}
            <TagInput
              ref={inputRef}
              onChange={handleInputChange}
              value={inputValue}
              onKeyDown={handleKeyDown}
            />
          </AdornedTextField>
          <TagListContainer
            spacing={1}
            sx={{ height: "126px", overflowY: "auto" }}
          >
            {!isEmpty(suggestedTags) && (
              <Stack>
                {suggestedTags.map((suggestedTag) => {
                  const isNewTag = isNewSuggestedTag(
                    suggestedTag,
                    unsavedAppliedTags
                  );
                  return (
                    <SelectableTag
                      key={suggestedTag.id}
                      {...suggestedTag}
                      isNew={isNewTag}
                      onColorChange={
                        isNewTag
                          ? handleTagColorChange(suggestedTag.id)
                          : undefined
                      }
                      onClick={() => handleSelectSuggestedTag(suggestedTag)}
                    />
                  );
                })}
                <Divider />
              </Stack>
            )}
            <TagList>
              {unappliedTags.map((tag) => (
                <Tag
                  key={tag.id}
                  tag={tag}
                  onClick={() => handleSelectExistingTag(tag)}
                />
              ))}
            </TagList>
          </TagListContainer>
          {canApplyToSeries && (
            <Row variant="apart">
              <ApplyToSeries
                checked={applyToSeries}
                onChange={handleToggleApplyToSeries}
              />
              <TooltipIcon
                title="Applies selected tags to all past and future meetings for this recurring event."
                placement="top-end"
              />
            </Row>
          )}
          <Row spacing={1}>
            <ActionButton onClick={handleClose}>Cancel</ActionButton>
            <ActionButton onClick={handleSave} variant="contained">
              Save
            </ActionButton>
          </Row>
        </PopoverContent>
      </Popover>
    </>
  );
};

export default ManageTagsPopover;
